import { useState, useEffect } from "react";

import {
  Button,
  Radio,
  Stack,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Tab,
} from "@mui/material";
import { SubmitActions } from "./SubmitActions";
import { DataGrid, GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import {
  Add,
  Block,
  Close,
  CheckCircle,
  HighlightOffRounded,
  Reply,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";

import { useViewContext } from "../../../contexts/ViewContext";
import { toast } from "react-toastify";
import { useLocationContext } from "../../../contexts/LocationContext";
import { useFhirData } from "../../../utils/useFhirData";
import { PutLocation } from "../../../services/fhir/location/PutLocation";
import { PatchLocation } from "../../../services/fhir/location/PatchLocation";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { InativateConfirmDialog } from "./InativateConfirmDialog";

export function SelectLocation({
  setLocationSelected,
  setCurrentStep,
  setValue,
  handleSubmit,
  onSubmit,
  getValues,
  refetchLocation,
  selectedTab = "1",
  setSelectedTab,
}: any) {
  const { location } = useLocationContext();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [locationToDeactivate, setLocationToDeactivate] = useState<any>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [inactivateDialogOpen, setInactivateDialogOpen] = useState(false);
  const [locationIdToInactivate, setLocationIdToInactivate] = useState<
    string | null
  >(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  
  // Armazenar seleções para cada tab separadamente
  const [activeTabSelection, setActiveTabSelection] = useState<string[]>([]);
  const [inactiveTabSelection, setInactiveTabSelection] = useState<string[]>([]);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const clickNext = async (data: any) => {
    await onSubmit(data);
    setCurrentStep((prev: number) => prev + 1);
  };

  const formatAddress = (addressData?: any) => {
    const auxAddress = addressData;
    const city = auxAddress?.city;
    const state = auxAddress?.state;
    const postalCode = auxAddress?.postalCode;

    return auxAddress.line
      ? auxAddress?.line?.join(", ")
      : Object.keys(auxAddress)?.length === 1
      ? "Teleconsulta"
      : `${city && city}, ${state && state}, ${postalCode && postalCode}`;
  };

  const fillAddress = (name: string, address: string) => {
    const fieldsAddress: Array<string> = [
      "name",
      "city",
      "state",
      "cep",
      "number",
      "street",
      "complement",
      "zone",
      "locationId",
    ];

    const addressFormatter = address?.split(",");

    const valuesOrganization: Array<string> = [
      name,
      addressFormatter?.[0]?.replace(" ", "") || "",
      addressFormatter?.[1]?.replace(" ", "") || "",
      addressFormatter?.[2]?.replace(" ", "") || "",
      addressFormatter?.[3]?.replace(" ", "") || "",
      addressFormatter?.[4]?.replace(" ", "") || "",
      addressFormatter?.[5]?.replace(" ", "") || "",
      addressFormatter?.[6]?.replace(" ", "") || "",
      selectedRow?.[0]?.id || "",
    ];

    fieldsAddress.map((el, index) =>
      setValue(`${el}`, valuesOrganization[index])
    );
  };

  const handleDeactivateLocation = (locationId: string) => {
    setLocationIdToInactivate(locationId);
    setInactivateDialogOpen(true);
  };

  const confirmDeactivateLocation = async () => {
    try {
      setDeactivateLoading(true);
      
      if (locationToDeactivate?.resource && locationToDeactivate.resource.id) {
        // Atualizar o status para inactive
        const updatedLocation = {
          ...locationToDeactivate.resource,
          status: "inactive",
        };

        await PutLocation(updatedLocation, locationToDeactivate.resource.id);
        toast.success("Localização desativada com sucesso!");
        await refetchLocation();
      } else {
        toast.error("Localização não encontrada!");
      }
    } catch (error) {
      toast.error("Erro ao desativar localização. Tente novamente.");
      console.error("Erro ao desativar localização:", error);
    } finally {
      setDeactivateLoading(false);
      setConfirmDialogOpen(false);
      setLocationToDeactivate(null);
    }
  };

  /**
   * Verifica se uma location é de domicílio.
   * Domicílio usa a extension attendance-channel=domicilio e não aparece na lista presencial.
   */
  const isDomicilio = (resource: fhir5.Location | undefined) =>
    resource?.extension?.some(
      (ext) =>
        ext.url ===
          "https://fluxmed.com.br/fhir/StructureDefinition/attendance-channel" &&
        ext.valueCode === "domicilio"
    );

  // Filtra apenas localizações presenciais (não virtuais e não domicílio).
  // Teleatendimento, Chat e Domicílio são resolvidos automaticamente pela
  // primeira tela de tipo de atendimento e não devem aparecer aqui.
  const activeRows = location
    ?.filter(
      (e: any) =>
        e.resource?.status === "active" &&
        !useFhirData.virtualService(e?.resource) &&
        !isDomicilio(e?.resource)
    )
    ?.map((elLocation: fhir5.BundleEntry<fhir5.Location>) => {
      const data = {
        id: elLocation?.resource?.id,
        name: elLocation?.resource?.name,
        address: elLocation?.resource?.address
          ? formatAddress(elLocation?.resource?.address)
          : "-",
        isRemote: false,
      };
      return data;
    });

  const inactiveRows = location
    ?.filter(
      (e: any) =>
        e.resource?.status === "inactive" &&
        !useFhirData.virtualService(e?.resource) &&
        !isDomicilio(e?.resource)
    )
    ?.map((elLocation: fhir5.BundleEntry<fhir5.Location>) => {
      const data = {
        id: elLocation?.resource?.id,
        name: elLocation?.resource?.name,
        address: elLocation?.resource?.address
          ? formatAddress(elLocation?.resource?.address)
          : "-",
        isRemote: false,
      };
      return data;
    });

  // Inicializar as seleções se estiverem vazias e houver linhas disponíveis
  useEffect(() => {
    if (activeTabSelection.length === 0 && activeRows && activeRows.length > 0) {
      const id = activeRows[0]?.id;
      if (id) {
        setActiveTabSelection([id]);
      }
    }
    
    if (inactiveTabSelection.length === 0 && inactiveRows && inactiveRows.length > 0) {
      const id = inactiveRows[0]?.id;
      if (id) {
        setInactiveTabSelection([id]);
      }
    }
  }, [activeRows, inactiveRows, activeTabSelection, inactiveTabSelection]);

  // Usar a seleção apropriada com base na tab atual
  const selectionModel = selectedTab === "1" ? activeTabSelection : inactiveTabSelection;
  
  // Atualizar a seleção da tab apropriada quando o modelo de seleção muda
  const handleSelectionModelChange = (newSelectionModel: any) => {
    if (selectedTab === "1") {
      setActiveTabSelection(newSelectionModel);
    } else {
      setInactiveTabSelection(newSelectionModel);
    }
  };

  // Determinar a linha selecionada com base na tab atual
  const selectedRow = (selectedTab === "1" ? activeRows : inactiveRows)?.filter(
    (item: any) => {
      return item?.id === selectionModel[0];
    }
  );

  const { availableRoles } = useViewContext();

  const handleReactivateLocation = async () => {
    if (selectedRow?.[0]?.id && selectedTab === "2") {
      try {
        setReactivateLoading(true);
        // Reativar a localização usando PatchLocation
        await PatchLocation({
          id: selectedRow?.[0]?.id,
          operation: "replace",
          path: "/status",
          content: "active",
        });
        
        toast.success("Localização reativada com sucesso!");
        await refetchLocation();
        
        // Continuar com o fluxo normal
        fillAddress(
          selectedRow?.[0]?.name || "",
          selectedRow?.[0]?.address
        );
        setValue("locationFilled", false);
        
        const submitFunction = handleSubmit(clickNext);
        submitFunction();
      } catch (error) {
        console.error("Erro ao reativar localização:", error);
      } finally {
        setReactivateLoading(false);
      }
    } else {
      toast.warning("Selecione uma localização para reativar");
    }
  };

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        {selectedTab === "1" ? (
          <Button
            fullWidth
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "flex-start",
              color: "neutral1000.main",
              gap: 1,
              textTransform: "initial",
            }}
            onClick={() => {
              setLocationSelected(true);
              setValue("locationFilled", true);
              setValue("locationId", undefined);
            }}
          >
            <Add sx={{ marginLeft: 1 }} />
            Adicionar nova localização
          </Button>
        ) : (
          <Button
            fullWidth
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "flex-start",
              alignItems: "center",
              color: "neutral1000.main",
              gap: 1,
              textTransform: "initial",
              pl: 1,
            }}
            onClick={handleReactivateLocation}
            disabled={reactivateLoading}
          >
            <Reply sx={{ marginLeft: 2 }} />

            {reactivateLoading
              ? "Reativando..."
              : "Reativar localização selecionada"}
          </Button>
        )}
        <GridFooter
          sx={{
            border: "none",
          }}
        />
      </GridFooterContainer>
    );
  };

  // Altura fixa para os DataGrids
  const dataGridHeight = 400;

  // Definição das colunas para a tabela de localizações ativas
  const activeColumns = [
    {
      field: "radiobutton",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: (params: any) => (
        <Radio checked={selectionModel[0] === params.id} value={params.id} />
      ),
    },
    {
      field: "name",
      headerName: "Nome",
      flex: 0.5,
    },
    {
      field: "address",
      headerName: "Endereço",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 100,
      sortable: false,
      renderCell: (params: any) => (
        <Tooltip title="Desativar localização">
          <IconButton 
            onClick={(e) => {
              e.stopPropagation();
              handleDeactivateLocation(params.id);
            }}
            size="small"
            color="error"
          >
            <Close />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Definição das colunas para a tabela de localizações inativas
  const inactiveColumns = [
    {
      field: "radiobutton",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: (params: any) => (
        <Radio checked={selectionModel[0] === params.id} value={params.id} />
      ),
    },
    {
      field: "name",
      headerName: "Nome",
      flex: 0.5,
    },
    {
      field: "address",
      headerName: "Endereço",
      flex: 1,
    },
  ];

  return (
    <>
      <Stack width="100%" spacing={2} mb={2}>
        <Typography
          variant="h6"
          fontWeight="600"
          color="neutral700.main"
          textAlign="center"
          padding="16px 0px 16px 0px"
        >
          {availableRoles?.includes("manager")
            ? "Selecione ou adicione uma localização"
            : "Selecione uma localização"}
        </Typography>

        <TabContext value={selectedTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <TabList onChange={handleChangeTab} sx={{ width: "100%" }}>
              <Tab
                style={{ display: "flex", alignItems: "center" }}
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <CheckCircle sx={{ marginRight: 1 }} />
                    Ativas
                  </Box>
                }
                value="1"
              />

              <Tab
                style={{ display: "flex", alignItems: "center" }}
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <HighlightOffRounded sx={{ marginRight: 1 }} />
                    Inativas
                  </Box>
                }
                value="2"
              />
            </TabList>
          </Box>
          
          <TabPanel value="1" sx={{ padding: 0, mt: 2 }}>
            <div style={{ height: dataGridHeight, width: '100%' }}>
              <DataGrid
                rows={activeRows || []}
                columns={activeColumns}
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                hideFooterPagination
                hideFooter={!availableRoles?.includes("manager")}
                hideFooterSelectedRowCount
                localeText={{
                  noRowsLabel: "Você ainda não possui registros ativos",
                }}
                rowSelectionModel={selectionModel}
                slots={
                  availableRoles?.includes("manager") && {
                    footer: CustomFooter,
                  }
                }
                onRowSelectionModelChange={handleSelectionModelChange}
                sx={{
                  "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                  },
                }}
              />
            </div>
          </TabPanel>
          
          <TabPanel value="2" sx={{ padding: 0, mt: 2 }}>
            <div style={{ height: dataGridHeight, width: '100%' }}>
              <DataGrid
                rows={inactiveRows || []}
                columns={inactiveColumns}
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                hideFooterPagination
                hideFooter={!availableRoles?.includes("manager")}
                hideFooterSelectedRowCount
                localeText={{
                  noRowsLabel: "Você ainda não possui registros inativos",
                }}
                rowSelectionModel={selectionModel}
                slots={
                  availableRoles?.includes("manager") && {
                    footer: CustomFooter,
                  }
                }
                onRowSelectionModelChange={handleSelectionModelChange}
                sx={{
                  "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                  },
                }}
              />
            </div>
          </TabPanel>
        </TabContext>

        <SubmitActions
          loading={reactivateLoading}
          // Voltar ao step de tipo de atendimento (S0AttendanceType)
          goBack={() => setCurrentStep((prev: number) => prev - 1)}
          goForward={() => {
            if (selectedTab === "2") {
              handleReactivateLocation();
            } else {
              fillAddress(
                selectedRow?.[0]?.name || "",
                selectedRow?.[0]?.address
              );
              setValue("locationFilled", false);
              
              if (selectedRow?.[0]?.id === undefined) {
                toast.warning("Adicione uma localização");
              } else {
                const submitFunction = handleSubmit(clickNext);
                submitFunction();
              }
            }
          }}
        />
      </Stack>
      
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Desativar localização</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja desativar a localização "
            {locationToDeactivate?.resource?.name}"?
            <br />
            Localizações desativadas não aparecerão nas listagens de
            agendamento.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialogOpen(false)} 
            color="primary"
            disabled={deactivateLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={confirmDeactivateLocation} 
            color="error" 
            autoFocus
            disabled={deactivateLoading}
          >
            {deactivateLoading ? "Desativando..." : "Desativar"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {inactivateDialogOpen && locationIdToInactivate && (
        <InativateConfirmDialog
          isOpen={inactivateDialogOpen}
          setIsOpen={setInactivateDialogOpen}
          locationId={locationIdToInactivate}
        />
      )}
    </>
  );
}
